// SA-8637: Sentry loaded from CDN (see src/index.html + src/types/sentry-cdn.d.ts).
import { environment } from '../../../environments/environment';
var ScriptInjectorService = /** @class */ (function () {
    function ScriptInjectorService() {
        this.scripts = [];
        // SA-8595: in-flight + resolved manifest URL cache keyed by MFE name.
        // Concurrent first-callers share one fetch Promise.
        this.resolvedUrls = new Map();
    }
    ScriptInjectorService.prototype.loadScript = function (name, fallbackUrl) {
        var _this = this;
        var _a;
        // Existing dedupe-by-name: if this script is already loaded or in flight,
        // return that Promise.
        var existingScript = this.scripts.find(function (s) { return s.name === name; });
        if (existingScript) {
            if (existingScript.loaded) {
                return Promise.resolve();
            }
            // The in-flight script's promise resolves when the <script> tag loads.
            return (_a = existingScript.promise) !== null && _a !== void 0 ? _a : Promise.resolve();
        }
        // SA-8595: resolve via per-MFE manifest with in-memory cache + concurrent-
        // caller dedupe, then inject the <script> tag.
        return this.resolveUrl(name, fallbackUrl).then(function (_a) {
            var url = _a.url;
            return _this.injectScript(name, url);
        });
    };
    ScriptInjectorService.prototype.resolveUrl = function (name, fallbackUrl) {
        var cached = this.resolvedUrls.get(name);
        if (cached) {
            return cached;
        }
        // The TS 3.8 root tsconfig doesn't know about `mfeCdnBaseUrl` on the
        // Environment shape, so we read via cast. Empty / undefined => skip
        // the manifest fetch and use the fallback URL directly.
        var baseUrl = environment.mfeCdnBaseUrl;
        if (!baseUrl) {
            var direct = { url: fallbackUrl, source: 'fallback' };
            var p = Promise.resolve(direct);
            this.resolvedUrls.set(name, p);
            return p;
        }
        var manifestUrl = baseUrl + "/" + name + ".manifest.json";
        var promise = fetch(manifestUrl, {
            cache: 'no-store'
        })
            .then(function (res) {
            if (!res.ok) {
                throw new Error("manifest fetch failed: HTTP " + res.status);
            }
            return res.json();
        })
            .then(function (json) {
            if (!json || typeof json.main !== 'string' || json.main.length === 0) {
                throw new Error("manifest missing 'main' field: " + JSON.stringify(json));
            }
            return { url: baseUrl + "/" + json.main, source: 'manifest' };
        })
            .catch(function (err) {
            console.error("ScriptInjectorService: manifest fetch failed for " + name + ", " +
                "falling back to legacy URL.", { manifestUrl: manifestUrl, fallbackUrl: fallbackUrl, error: err });
            return { url: fallbackUrl, source: 'fallback' };
        });
        this.resolvedUrls.set(name, promise);
        return promise;
    };
    ScriptInjectorService.prototype.injectScript = function (name, src) {
        var _this = this;
        return new Promise(function (resolve, reject) {
            var scriptElement = document.createElement('script');
            scriptElement.type = 'text/javascript';
            scriptElement.src = src;
            scriptElement.async = true;
            var script = {
                name: name,
                src: src,
                loaded: false
            };
            // SA-8595: capture the load outcome on `script.promise` so future
            // in-flight callers (via the dedupe-by-name path in loadScript) can
            // await this load instead of re-injecting a duplicate <script>.
            var promise = new Promise(function (res, rej) {
                scriptElement.onload = function () {
                    script.loaded = true;
                    res();
                    resolve();
                };
                scriptElement.onerror = function (event, srcAttr, lineno, colno, error) {
                    var loadError = new Error("Failed to load script: " + name);
                    // SA-8637: defensive guard — see sentry.utils.ts.
                    if (typeof Sentry !== 'undefined') {
                        Sentry.captureException(loadError, {
                            tags: { source: 'script-loader', mfe: name },
                            extra: { src: src }
                        });
                    }
                    console.error(loadError);
                    rej(loadError);
                    reject(loadError);
                };
            });
            script.promise = promise;
            document.body.appendChild(scriptElement);
            _this.scripts.push(script);
        });
    };
    return ScriptInjectorService;
}());
export { ScriptInjectorService };
